import moment from 'moment';
import {
  dineAroundRules as RULES,
  dineAroundBreakfastType,
  dineAroundDinnerType,
} from './dineAroundConstants';
import { getEntertainmentDates } from '../utils';

export const getDineAroundFormatByType = dineAroundType => (dineAroundType === dineAroundBreakfastType ? 'Breakfast' : 'Dinner');

export const getDineAroundTypeByEntertainment = (
  categoryId,
  dineAroundBreakfastCategory,
  dineAroundDinnerCategory,
) => {
  const parsedCategory = parseInt(categoryId, 10);
  const parsedDineAroundBreakfastCategory = parseInt(dineAroundBreakfastCategory, 10);
  const parsedDineAroundDinnerCategory = parseInt(dineAroundDinnerCategory, 10);
  if (parsedCategory === parsedDineAroundBreakfastCategory) return dineAroundBreakfastType;
  if (parsedCategory === parsedDineAroundDinnerCategory) return dineAroundDinnerType;
  return null;
};

const getIsOpenOnThatDay = (timeSlots, selectedDate) => timeSlots.some(({ Starts: startDate }) => moment(selectedDate).isSame(moment(startDate), 'day'));

export const getOpenVenuesAcrossTheBreak = (
  arrivalDate,
  departureDate,
  entertainments = [],
  dineAroundCategory,
  dineAroundType = dineAroundBreakfastType,
  today = moment(),
) => {
  if (!arrivalDate || !departureDate || entertainments.length === 0) return [];
  const openVenues = getEntertainmentDates(arrivalDate, departureDate, true, today);
  const filteredEntertainments = entertainments.filter(({ CategoryId: categoryId }) => parseInt(categoryId, 10) === parseInt(dineAroundCategory, 10));
  filteredEntertainments.forEach(({ Locations: locations = [] }) => {
    locations.forEach(({ Name: VenueName, Id: VenueId, TimeSlots: timeSlots }) => {
      openVenues.forEach(({ date, venues }) => {
        const isOpenOnThatDay = getIsOpenOnThatDay(timeSlots, date);
        if (isOpenOnThatDay) {
          venues.push({
            id: VenueId,
            name: VenueName,
          });
        }
      });
    });
  });
  const filteredVenuesBasedOnCategory = dineAroundType === dineAroundBreakfastType
    ? openVenues.slice(1)
    : openVenues.slice(0, -1);
  return filteredVenuesBasedOnCategory.filter(({ venues }) => venues.length > 0);
};

const getNumberOfVenues = (openVenues = []) => {
  if (!openVenues || openVenues.length === 0) return 0;
  const uniqueVenus = openVenues.reduce((uniqueVenues, { venues }) => {
    venues.forEach(({ id }) => {
      if (!uniqueVenues.includes(id)) uniqueVenues.push(id);
    });
    return uniqueVenues;
  }, []);
  return uniqueVenus.length;
};

const getDayType = day => moment(day).format('ddd');

const getIndexForSplittingVenues = (arrivalDate, firstPartOfSevenNights = true, isFirst = true) => {
  const arrivalDateType = getDayType(arrivalDate).toLowerCase();
  if (firstPartOfSevenNights) {
    if (isFirst) return 0;
    return arrivalDateType === 'mon' ? 4 : 3; // first 4 or 3 nights
  }
  if (isFirst) return arrivalDateType === 'mon' ? 4 : 3; // second 4 or 3 nights
  return 7;
};

const getCurrentPartOfNights = (openVenues = [], arrivalDate, firstPartOfSevenNights = true) => {
  const firstIndex = getIndexForSplittingVenues(arrivalDate, firstPartOfSevenNights);
  const secondIndex = getIndexForSplittingVenues(arrivalDate, firstPartOfSevenNights, false);
  return openVenues.slice(firstIndex, secondIndex);
};

export const getRuleBasedOnOpenVenues = (openVenues = [], arrivalDate, isDABreakfast = true, firstPartOfSevenNights = true) => {
  if (!isDABreakfast || openVenues.length === 0) return RULES.noRestriction; // DineAround dinners does not include restrictions
  const numberOfNights = openVenues.length;
  const numberOfVenues = getNumberOfVenues(openVenues);
  if (numberOfVenues === 1) return RULES.noRestriction;
  switch (numberOfNights) {
    case 2: {
      if (numberOfVenues === 2) return RULES.oneAtEach;
      if (numberOfVenues === 3 || numberOfVenues === 4) return RULES.noMoreThanOneAtAVenue;
      break;
    }
    case 3: {
      if (numberOfVenues === 2) return RULES.oneAtEachSatSunFreeMonday;
      if (numberOfVenues === 3) return RULES.oneAtEach;
      if (numberOfVenues === 4) return RULES.noMoreThanOneAtAVenue;
      break;
    }
    case 4: {
      if (numberOfVenues === 2) return RULES.twoAtEach;
      if (numberOfVenues === 3) return RULES.oneAtEachTueThuFreeFriday;
      if (numberOfVenues === 4) return RULES.oneAtEach;
      break;
    }
    case 7: {
      const currentPartOfNights = getCurrentPartOfNights(openVenues, arrivalDate, firstPartOfSevenNights);
      return firstPartOfSevenNights
        ? getRuleBasedOnOpenVenues(currentPartOfNights, arrivalDate, isDABreakfast)
        : getRuleBasedOnOpenVenues(currentPartOfNights, arrivalDate, isDABreakfast, false);
    }
    default: {
      return RULES.noRestriction;
    }
  }
  return RULES.noRestriction;
};

export const getIsDineAroundEvent = (
  categoryId,
  isDaBooking,
  dineAroundBreakfastCategory,
  dineAroundDinnerCategory,
) => {
  if (!isDaBooking) return false;
  const parsedCategory = parseInt(categoryId, 10);
  const parsedDineAroundBreakfastCategory = parseInt(dineAroundBreakfastCategory, 10);
  const parsedDineAroundDinnerCategory = parseInt(dineAroundDinnerCategory, 10);
  return parsedCategory === parsedDineAroundBreakfastCategory
    || parsedCategory === parsedDineAroundDinnerCategory;
};

export const getDineAroundBookedItems = (
  bookedEventsOnThisDay,
  type = dineAroundBreakfastType,
) => bookedEventsOnThisDay.filter(({ dineAroundType }) => dineAroundType === type);

export const getOpenVenuesWithBookedItems = (openVenues = [], bookedDAItemsInCategory = []) => {
  if (!openVenues || !bookedDAItemsInCategory) return [];
  return openVenues.map((venueWithDate) => {
    const { date, venues } = venueWithDate;
    const bookedItemsOnThisDate = bookedDAItemsInCategory.filter(({ startDate }) => moment(startDate).isSame(moment(date), 'day'));
    const newVenue = venues.map((venue) => {
      const { name: locationName } = venue;
      const bookedItemsOnThisVenue = bookedItemsOnThisDate.filter(({ location: venueName }) => locationName === venueName);
      return {
        ...venue,
        numOfBookedItems: bookedItemsOnThisVenue.length,
      };
    });
    return {
      ...venueWithDate,
      venues: newVenue,
    };
  });
};

const getBookedItemsFromVenues = (filteredVenues = []) => filteredVenues.reduce(
  (sum, { numOfBookedItems = 0 }) => sum + numOfBookedItems, 0,
);

const filterFreeFlowDay = (openVenuesWithBookedItems = [], freeFlowDay = null) => {
  if (!freeFlowDay) return openVenuesWithBookedItems;
  return openVenuesWithBookedItems.filter(({ date }) => {
    const dateType = getDayType(date);
    return dateType.toLowerCase() !== freeFlowDay.toLowerCase();
  });
};

export const getNumberOfBookedItemsForSelectedVenue = (
  openVenuesWithBookedItems = [],
  selectedLocation,
  arrivalDate,
  firstPartOfSevenNights = true,
  freeFlowDay = null,
) => {
  const numberOfNights = openVenuesWithBookedItems.length;
  const currentPartOfNights = numberOfNights < 7
    ? openVenuesWithBookedItems
    : getCurrentPartOfNights(openVenuesWithBookedItems, arrivalDate, firstPartOfSevenNights);

  return filterFreeFlowDay(currentPartOfNights, freeFlowDay).reduce(
    (numOfBookedItemsForSelected, { venues }) => {
      const filteredVenues = venues.filter(({ name: locationName }) => locationName === selectedLocation);
      return numOfBookedItemsForSelected + getBookedItemsFromVenues(filteredVenues);
    },
    0,
  );
};

const getVenuesGroupByBookedItems = (openVenuesWithBookedItems = [], selectedDate) => {
  const groupVenues = openVenuesWithBookedItems.reduce(
    (newVenues, { venues, date }) => {
      const extendedVenues = newVenues;
      if (selectedDate) {
        const formattedDate = moment(date);
        const formattedSelectedDate = moment(selectedDate);
        const isSame = formattedDate.isSame(formattedSelectedDate, 'day');
        if (!isSame) return extendedVenues;
      }
      venues.forEach(({ id, numOfBookedItems }) => {
        if (numOfBookedItems > 0) extendedVenues[id] = extendedVenues[id] ? extendedVenues[id] + numOfBookedItems : numOfBookedItems;
      });
      return extendedVenues;
    },
    {},
  );
  return groupVenues;
};

const getBookedItemsOnThisDay = (openVenuesWithBookedItems, date) => {
  const groupedVenuesOnThisDay = getVenuesGroupByBookedItems(openVenuesWithBookedItems, date);
  return Object.keys(groupedVenuesOnThisDay).filter(key => groupedVenuesOnThisDay[key] > 0).length > 0;
};

const getSelectedActivityIsWithinTheFirstPartOfSevenNights = (openVenues = [], selectedDate, arrivalDate) => {
  const numberOfNights = openVenues.length;
  if (numberOfNights < 7) return false;
  const arrivalDateType = getDayType(arrivalDate).toLowerCase();
  const selectedDateMoment = moment(selectedDate);
  const selectedArrivalDate = moment(arrivalDate);
  const diff = selectedDateMoment.diff(selectedArrivalDate, 'day');
  return arrivalDateType === 'mon' ? diff < 5 : diff < 4;
};

const getFreeFlowDay = (rule = RULES.noRestriction) => {
  if (rule === RULES.oneAtEachSatSunFreeMonday) return 'mon';
  if (rule === RULES.oneAtEachTueThuFreeFriday) return 'fri';
  return null;
};

export const getIsAllowedToBookThisDineAround = (
  bookedDAItemsInCategory = [],
  selectedEntertainment = {},
  arrivalDate,
) => {
  const { dineAroundType, openVenues = [], location: selectedLocation, date } = selectedEntertainment;
  if (bookedDAItemsInCategory.length === 0) return true;
  if (!openVenues || openVenues.length === 0) return false;
  const openVenuesWithBookedItems = getOpenVenuesWithBookedItems(openVenues, bookedDAItemsInCategory);
  const hasAlreadyBookedItemOnThisDay = getBookedItemsOnThisDay(openVenuesWithBookedItems, date);
  if (hasAlreadyBookedItemOnThisDay) return false;

  const selectedActivityIsWithinTheFirstPartOfSevenNights = getSelectedActivityIsWithinTheFirstPartOfSevenNights(openVenues, date, arrivalDate);
  const rule = getRuleBasedOnOpenVenues(openVenues, arrivalDate, dineAroundType === dineAroundBreakfastType, selectedActivityIsWithinTheFirstPartOfSevenNights);
  if (rule === RULES.noRestriction) return true;

  const numberOfBookedItems = getNumberOfBookedItemsForSelectedVenue(
    openVenuesWithBookedItems,
    selectedLocation,
    arrivalDate,
    selectedActivityIsWithinTheFirstPartOfSevenNights,
    getFreeFlowDay(rule),
  );

  switch (rule) {
    case RULES.oneAtEach: {
      return numberOfBookedItems < 1;
    }
    case RULES.twoAtEach: {
      return numberOfBookedItems < 2;
    }
    case RULES.noMoreThanOneAtAVenue: {
      return numberOfBookedItems < 1;
    }
    case RULES.oneAtEachSatSunFreeMonday: {
      const dayType = getDayType(date);
      return numberOfBookedItems < 1 || dayType.toLowerCase() === 'mon';
    }
    case RULES.oneAtEachTueThuFreeFriday: {
      const dayType = getDayType(date);
      return numberOfBookedItems < 1 || dayType.toLowerCase() === 'fri';
    }
    default: {
      return false;
    }
  }
};

const dineAroundLatestArrivalDateType = 'dineAroundLatestArrivalDateType';
const dineAroundNumberOfDaysBeforeType = 'dineAroundNumberOfDaysBeforeType';

const getCalculatedDateToUseDineAround = (numberOfDaysBeforeDineAround, latestArrivalDate, today = moment()) => {
  const momentNumberOfDaysBeforeLogin = today.add(numberOfDaysBeforeDineAround, 'd');
  const momentLatestArrivalDate = moment(latestArrivalDate);
  return momentLatestArrivalDate.isBefore(momentNumberOfDaysBeforeLogin) ? {
    date: momentLatestArrivalDate,
    type: dineAroundLatestArrivalDateType,
  } : {
    date: momentNumberOfDaysBeforeLogin,
    type: dineAroundNumberOfDaysBeforeType,
  };
};

// TODO: We ignore the dineAroundBackstopDate stuffs at the moment, but we're checking them here
export const getIsDineAroundAllowed = (config, arrivalDate, seasonSuffix = '', today = moment()) => {
  const {
    [`REACT_APP_DINE_AROUND_NUMBER_OF_DAYS_BEFORE${seasonSuffix}`]: dineAroundNumberOfDaysBefore,
    REACT_APP_DINE_AROUND_BOOKING_ENABLE: dineAroundBookingEnable,
    [`REACT_APP_DINE_AROUND_LATEST_ARRIVAL_DATE${seasonSuffix}`]: dineAroundLatestArrivalDate,
    // REACT_APP_DINE_AROUND_BACKSTOP_DATE: dineAroundBackStopDate,
  } = config;
  if (dineAroundBookingEnable !== 'true') {
    return {
      isAllowed: false,
      restrictedByBackstopDate: false,
      backstopDate: '',
    };
  }
  const numberOfDaysBeforeDineAround = parseInt(dineAroundNumberOfDaysBefore, 10);
  const { date: calculatedDate, type: calculatedType } = getCalculatedDateToUseDineAround(numberOfDaysBeforeDineAround, dineAroundLatestArrivalDate, today);
  const momentArrivalDate = moment(arrivalDate);
  if (calculatedDate.isBefore(momentArrivalDate, 'day')) {
    return {
      isAllowed: false,
      restrictedByBackstopDate: false,
      backstopDate:  '' ,
    };
  }
  else{
  return {
    isAllowed: true,
    restrictedByBackstopDate: false,
    backstopDate: '',
  };
}
};

export const getShouldDineAroundSkip = ({ wasCalled, dineAroundItems }, { isDaBooking, arrivalDate, seasonSuffix = '' }, config) => {
  const { isAllowed: isDineAroundAllowed } = getIsDineAroundAllowed(config, arrivalDate, seasonSuffix);
  return wasCalled && (!dineAroundItems || dineAroundItems.length === 0 || !isDaBooking || !isDineAroundAllowed);
};
